/**
 * Repointing a folder document after its files are renamed.
 *
 * Nodes reach their file by index (`externalAssets`, `extras.trace.file`), but
 * behaviors name files by path, so a rename has to follow every behavior
 * string the exporter collected as a file (`_collectAssetPathsDeep`): exact
 * equality, with or without a leading `./`. Provenance history
 * (`isProvenanceHistoryField`) is carried as written.
 */

import { NESTED_SRC_RECURSION_DEPTH, isProvenanceHistoryField } from './behaviorAssetFields';

/**
 * `behaviors` with every path in `moved` replaced by its new one. `moved`
 * carries each spelling it renames (`assets/x` and `./assets/x`). Same reach
 * and cycle guard as the exporter's collector; the behaviors array is one
 * level above each behavior.
 */
export function repointBehaviorPaths<T>(behaviors: T, moved: ReadonlyMap<string, string>): T {
    if (moved.size === 0) return behaviors;
    const visited = new WeakSet<object>();
    const repoint = (value: unknown, depth: number): unknown => {
        if (typeof value === 'string') return moved.get(value) ?? value;
        if (!value || typeof value !== 'object' || depth > NESTED_SRC_RECURSION_DEPTH || visited.has(value)) return value;
        visited.add(value);
        if (Array.isArray(value)) return value.map((entry) => repoint(entry, depth + 1));
        return Object.fromEntries(Object.entries(value).map(([key, entry]) => [
            key, isProvenanceHistoryField(value as Record<string, unknown>, key) ? entry : repoint(entry, depth + 1),
        ]));
    };
    return repoint(behaviors, -1) as T;
}

/**
 * `document` repointed at the names its files were stored under: each
 * `files[k].uri` in `mapping`, and every behavior path naming one. Keys are
 * the document's own relative uris (`assets/…`). The input is not mutated, so
 * a save that fails partway leaves the caller's document intact.
 */
export function repointFolderDocument<T>(document: T, mapping: ReadonlyMap<string, string>): T {
    const doc = document as { files?: unknown; nodes?: unknown };
    if (!doc || !Array.isArray(doc.files) || mapping.size === 0) return document;
    const moved = new Map<string, string>();
    const files = doc.files.map((entry: unknown) => {
        const uri = (entry as { uri?: unknown } | null)?.uri;
        const next = typeof uri === 'string' ? mapping.get(uri) : undefined;
        if (next === undefined || next === uri) return entry;
        moved.set(uri as string, next);
        moved.set(`./${uri as string}`, `./${next}`);
        return { ...(entry as object), uri: next };
    });
    const nodes = moved.size > 0 && Array.isArray(doc.nodes)
        ? doc.nodes.map((node: unknown) => {
            const trace = (node as { extras?: { trace?: { behaviors?: unknown } } } | null)?.extras?.trace;
            if (!trace?.behaviors) return node;
            const record = node as { extras: Record<string, unknown> };
            return { ...record, extras: { ...record.extras, trace: { ...trace, behaviors: repointBehaviorPaths(trace.behaviors, moved) } } };
        })
        : doc.nodes;
    return { ...doc, files, ...(nodes === undefined ? {} : { nodes }) } as T;
}
